import { IconName, MaterialSymbolIcon } from '../material-symbol-link';
import { ReactNode, useRef, useState } from 'react';
import { useTranslation } from '@/contexts/i18n-context';
import { BaseInputWrapper, WarningOnChangeConfig } from './base-input-wrapper';
import clsx from 'clsx';
import baseStyles from './base-input-wrapper.module.css';
import textInputStyles from './text-input.module.css';

interface BaseTextInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  name: string;
  withIconsIndicators?: boolean;
  withErrorMessage?: boolean;
  description?: string;
  warningOnChange?: WarningOnChangeConfig;
  inputClassName?: string;
  icon?: IconName;
  trimWhiteSpaces?: boolean;
  roundNumbers?: boolean;
  suffix?: string;
}

interface TextInputWithLabelProps extends BaseTextInputProps {
  label?: ReactNode;
  subLabel?: ReactNode;
  'aria-label'?: string;
}

interface TextInputWithoutLabelProps extends BaseTextInputProps {
  label?: never;
  subLabel?: never;
  'aria-label': string;
}

type TextInputProps = TextInputWithLabelProps | TextInputWithoutLabelProps;

export function TextInput({
  name,
  label,
  subLabel,
  type = 'text',
  className,
  inputClassName,
  withIconsIndicators = true,
  withErrorMessage = true,
  trimWhiteSpaces = false,
  roundNumbers = false,
  description,
  icon,
  suffix,
  warningOnChange,
  ...restProps
}: TextInputProps) {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const { t } = useTranslation();
  const isPassword = type === 'password';
  const isDate = type === 'date';
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDateIconClick = () => {
    if (inputRef.current) {
      inputRef.current.showPicker();
    }
  };

  return (
    <BaseInputWrapper
      name={name}
      label={label}
      subLabel={subLabel}
      className={className}
      withIconsIndicators={withIconsIndicators}
      withErrorMessage={withErrorMessage}
      description={description}
      warningOnChange={warningOnChange}
      additionalContainerClassName={isPassword || isDate ? baseStyles.hasRightIcon : undefined}
      restProps={{ ...restProps, type }}
      trimWhiteSpaces={trimWhiteSpaces}
      roundNumbers={roundNumbers}
    >
      {({ fieldProps, inputClassName: defaultInputClassName, htmlFor }) => (
        <>
          {icon && <MaterialSymbolIcon name={icon} className={textInputStyles.icon} />}
          <input
            id={htmlFor}
            className={clsx(
              defaultInputClassName,
              inputClassName,
              icon && textInputStyles.withIcon,
            )}
            {...fieldProps}
            type={isPassword ? (isPasswordVisible ? 'text' : 'password') : type}
            ref={(el) => {
              fieldProps.ref(el);
              inputRef.current = el;
            }}
          />
          {isDate && (
            <MaterialSymbolIcon
              name="calendar_today"
              className={baseStyles.dateIcon}
              onClick={handleDateIconClick}
              ariaLabel={'Select date'}
            />
          )}
          {isPassword && (
            <MaterialSymbolIcon
              name={isPasswordVisible ? 'visibility' : 'visibility_off'}
              className={baseStyles.passwordToggle}
              onClick={() => setIsPasswordVisible((prev) => !prev)}
              ariaLabel={t(isPasswordVisible ? 'common.hidePassword' : 'common.showPassword')}
            />
          )}
          {suffix && <span className={baseStyles.suffix}>{suffix}</span>}
        </>
      )}
    </BaseInputWrapper>
  );
}
